import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, message, Table, Tag } from "antd";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import { AxiosError, AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import queryString from "query-string";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { Opportunity } from "../helpers/shared-interfaces";
import {
  ProgressStatuses,
  request,
  responseHandler,
  getStateColor,
  fetchEnumOptions,
  capitalizeFirstLetter,
  showTotalPagination,
} from "../helpers/utilities";
import { DEBOUNCE_TIME, PAGE_SIZE } from "../helpers/constants";
import { PageToolbar } from "../components/PageToolbar";
import {
  hasActiveFilters,
  clearAllFilters,
  parseQueryParams,
  buildQueryString,
  handleFieldRemove,
} from "../components/TableFilters";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
  ADVANCED_QUERY,
} from "../components/AdvancedSearchModal";
import {
  SEARCH_OPPORTUNITY,
  GET_OPPORTUNITY_SORT_FIELDS,
  OPPORTUNITY_FORM_DATA,
  CREATE_OPPORTUNITY,
} from "../gql/opportunity";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface RouteParams {
  view?: string;
  filter?: string;
}

interface OpportunityQueryParams {
  for_current_user: string;
  page: number;
  q: string;
  state: string;
}

interface State {
  opportunities: Opportunity[];
  isLoading: boolean;
  opportuntiesCount: number;
  q: string;
  selectedStates: string[];
  forCurrentUser: boolean;
  page: number;
  pageSize: number;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string; }>;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export function getProgressStatus(progressStatus: string): string {
  if (progressStatus === ProgressStatuses.lead) {
    return "Lead";
  } else if (progressStatus === ProgressStatuses.won) {
    return "Won";
  } else if (progressStatus === ProgressStatuses.onboardingInitated) {
    return "PetraID Assigned";
  } else {
    return "In Progress";
  }
}

export const ColumnsCreatorHOC = (): ColumnType<Opportunity>[] => {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "company",
      key: "company",
      render(text: string, record: Opportunity): JSX.Element {
        const to =
          record?.progressStatus === ProgressStatuses.incomplete
            ? `/business-development/new-opportunity?opportunityId=${record?.id}`
            : `/business-development/opportunity/${record.id}`;
        if (record.customer) {
          return (
            <NavLink to={to}>
              {record.customer?.firstName ?? "No First Name"}{" "}
              {record.customer?.lastName ?? "No Last Name"}
            </NavLink>
          );
        } else {
          return <NavLink to={to}>{record.company?.name ?? "No Name"}</NavLink>;
        }
      },
    },
    {
      title: "Progress Status",
      dataIndex: "progressStatus",
      key: "progressStatus",
      sorter: (a: Opportunity, b: Opportunity): number => {
        return a.progressStatus.localeCompare(b.progressStatus);
      },
      ellipsis: true,
      render(text: string, record: Opportunity): JSX.Element {
        return (
          <Tag color={getStateColor(getProgressStatus(record.progressStatus))} key={text}>
            {getProgressStatus(record.progressStatus)}
          </Tag>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "opportunitySource",
      key: "opportunitySource",
      render(text: string, record: Opportunity): JSX.Element {
        return <span>{record.opportunitySource?.name}</span>;
      },
    },
    {
      title: "Prospect",
      dataIndex: "opportunityProspect",
      key: "opportunityProspect",
      render(text: string, record: Opportunity): JSX.Element {
        return <span>{record.opportunityProspect?.name}</span>;
      },
    },
    {
      title: "Product Name",
      dataIndex: "product",
      key: "product",
      render(text: string, record: Opportunity): JSX.Element {
        return <span>{record.product?.name}</span>;
      },
    },
    {
      title: "Assigned To",
      dataIndex: "assignee",
      key: "assignee",
      render(text: string, record: Opportunity): JSX.Element {
        return (
          <span>
            {record.assignee ? record?.assignee?.name : record?.createdBy?.name}
          </span>
        );
      },
    },
  ];
};

export default class Opportunities extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  filterReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      page: 1,
      pageSize: PAGE_SIZE,
      isLoading: true,
      opportunities: [],
      opportuntiesCount: 0,
      selectedStates: [],
      q: "",
      forCurrentUser: false,
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };

    this.filterReference = React.createRef<FormInstance>();

    this.redirectToNewOpportunityPage =
      this.redirectToNewOpportunityPage.bind(this);
    this.parseDate = this.parseDate.bind(this);

    this.getOpportunityCreationData =
      this.getOpportunityCreationData.bind(this);
    this.createOpportunity = this.createOpportunity.bind(this);
    this.nextPage = this.nextPage.bind(this);
  }

  createOpportunity(): Promise<AxiosResponse<void>> {
    return request({
      query: CREATE_OPPORTUNITY,
    });
  }

  getOpportunityCreationData(): Promise<AxiosResponse<void>> {
    return request({
      query: OPPORTUNITY_FORM_DATA,
    });
  }

  redirectToNewOpportunityPage(): void {
    this.setState({ isLoading: true });
    let creationData: Record<string, unknown>,
      newOpportunity: Record<string, unknown>;

    Promise.all([this.getOpportunityCreationData(), this.createOpportunity()])
      .then(([a, b]: [AxiosResponse<any>, AxiosResponse<any>]): void => {
        creationData = a.data;
        newOpportunity = b.data;

        this.setState({ isLoading: true });
        this.props.history.push("/business-development/new-opportunity", {
          opportunitySources: a.data.data.opportunitySources,
          opportunityProspects: a.data.data.opportunityProspects,
          countries: a.data.data.countries,
          opportunity: b.data.data.createOpportunity.opportunity,
        });
      })
      .catch((error: AxiosError): void => {
        Sentry.setExtras({
          creationData: JSON.stringify(creationData),
          newOpportunity: JSON.stringify(newOpportunity),
        });
        Sentry.captureException(error);
      });
  }

  parseDate(date: string): string {
    const fullDate = Date.parse(date);
    const newDate = new Date(fullDate);
    return newDate.toDateString();
  }

  async componentDidMount(): Promise<void> {
    const sortFieldOptions = await fetchEnumOptions(GET_OPPORTUNITY_SORT_FIELDS, {
      labelFormatter: (name) => capitalizeFirstLetter(name.toLowerCase().replace(/_/g, " ")),
      errorMsg: "Failed to fetch opportunity sort fields",
    });
    this.setState({ sortFieldOptions });
    const queryParams = parseQueryParams({ includeSort: true });
    const parsed = queryString.parse(window.location.search);
    this.setState({
      q: queryParams.q || "",
      selectedStates: queryParams.selectedStates || [],
      forCurrentUser: parsed.for_current_user === "true",
      page: queryParams.page || 1,
      pageSize: Number(parsed.pageSize) || PAGE_SIZE,
      sortField: queryParams.sortField || "CREATED_AT",
      sortDirection: queryParams.sortDirection || "DESC",
      selectedFieldsToDisplay: [],
    }, () => this.triggerImmediateSearch());
  }

  syncBrowserQueryString = (): void => {
    const { selectedFieldsToDisplay } = this.state;
    if (selectedFieldsToDisplay.length > 0) return;

    const queryStringResult = buildQueryString(this.state as any, {
      includeSort: true,
      includeType: true,
    });

    this.props.history.replace(queryStringResult);
  };

  handleSearchInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState({ q: event.target.value, page: 1 });
    this.triggerDelayedSearch();
  };

  handleSort = (sortField: string, sortDirection: string): void => {
    this.setState({ sortField, sortDirection, page: 1 }, () => this.triggerImmediateSearch());
  };

  handleFilterChange = (filterName: string, values: string[]): void => {
    if (filterName === "Status") {
      this.setState({ selectedStates: values, page: 1 }, () => this.triggerImmediateSearch());
    } else if (filterName === "Type") {
      this.setState({ forCurrentUser: values.includes("true"), page: 1 }, () => this.triggerImmediateSearch());
    }
  };

  handleOnFieldRemove = (field: IField): void => {
    handleFieldRemove(this, field, "opportunity");
  };

  handleAdvancedSearch = (data: AdvancedSearchReturn, fields: IField[]): void => {
    this.setState({
      selectedFieldsToDisplay: fields,
      opportunities: (data.results || []) as Opportunity[],
      opportuntiesCount: data.count || 0,
      showAdvancedSearchModal: false,
      page: 1,
      isLoading: false,
    });
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error(error);
    this.setState({ isLoading: false });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  async nextPage(page: number, pageSize?: number): Promise<void> {
    this.setState({ page, pageSize: pageSize || this.state.pageSize }, () => this.triggerImmediateSearch());
  }

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, selectedStates, forCurrentUser, page, pageSize, sortField, sortDirection, selectedFieldsToDisplay } = this.state;

    this.setState({ isLoading: true });

    try {
      let r: AxiosResponse;
      if (selectedFieldsToDisplay.length > 0) {
        r = await request({
          query: ADVANCED_QUERY,
          variables: {
            model: "opportunity",
            fields: selectedFieldsToDisplay.map(f => ({
              field: f.name,
              operator: f.operator,
              values: f.hasBetween ? [f.value, f.betweenValue as string] : [f.value],
            })),
            page,
            numberPerPage: pageSize,
          },
        });
      } else {
        r = await request({
          query: SEARCH_OPPORTUNITY,
          variables: {
            q,
            page,
            numberPerPage: pageSize,
            forCurrentUser,
            state: selectedStates.join(",") || null,
            orderBy: {
              field: sortField,
              direction: sortDirection,
            },
          },
        });
      }

      const queryKey = selectedFieldsToDisplay.length > 0 ? "advancedQuery" : "filter";
      const { err, mutation } = responseHandler<{ count: number; results: Opportunity[]; }>(r, queryKey);

      if (err) {
        message.error(`Failed To Load Opportunities - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.syncBrowserQueryString();
        this.setState({
          opportunities: mutation.results || [],
          opportuntiesCount: mutation.count || 0,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render(): JSX.Element | null {
    const {
      q,
      opportuntiesCount,
      opportunities,
      selectedStates,
      page,
      forCurrentUser,
      selectedFieldsToDisplay,
    } = this.state;

    const resultsWithKeys: Opportunity[] = (opportunities || []).map(
      (result: Opportunity): Opportunity => {
        return {
          ...result,
          key: result.id,
        };
      },
    );

    const hasFilters = hasActiveFilters(this.state as any);

    return (
      <>
        <Card>
          <PageToolbar
            searchValue={q}
            onSearchChange={this.handleSearchInput}
            searchPlaceholder="Search by id, name, source, prospect..."
            advancedSearch={{
              onOpen: () => this.setState({ showAdvancedSearchModal: true }),
              activeFields: selectedFieldsToDisplay,
              onFieldRemove: this.handleOnFieldRemove,
            }}
            primaryAction={
              <EntitledSection entitlement={entitlements.BusinessDevelopment.create}>
                <Button
                  className="base-button"
                  size="large"
                  onClick={this.redirectToNewOpportunityPage}
                  data-testid="create-opportunity-button"
                >
                  <PlusOutlined />
                  Create Opportunity
                </Button>
              </EntitledSection>
            }
            totalCount={opportuntiesCount}
            currentDataCount={opportunities.length}
            page={page}
            pageSize={this.state.pageSize}
            modelName="opportunities"
            filters={
              <PageToolbar.Filters
                isFiltering={hasFilters}
                onClear={() => clearAllFilters(this, "opportunity")}
                configs={[
                  {
                    label: "Progress Status",
                    options: [
                      { value: "completed", label: "Lead" },
                      { value: "in_progress", label: "In Progress" },
                      { value: "won", label: "Won" },
                      { value: "petra_id_assigned", label: "Petra Id Assigned" },
                    ],
                    selectedValues: selectedStates,
                    onChange: (values) => this.setState({ selectedStates: values, page: 1 }, () => this.triggerImmediateSearch()),
                    width: 180,
                  },
                  {
                    label: "Opportunity Type",
                    options: [
                      { value: "false", label: "All" },
                      { value: "true", label: "Assigned To Me" },
                    ],
                    selectedValues: forCurrentUser ? ["true"] : ["false"],
                    onChange: (values) => this.setState({ forCurrentUser: values.includes("true"), page: 1 }, () => this.triggerImmediateSearch()),
                    width: 180,
                  }
                ]}
                sort={{
                  field: this.state.sortField,
                  direction: this.state.sortDirection,
                  options: this.state.sortFieldOptions,
                  onChange: this.handleSort,
                }}
              />
            }
          />
          <Fragment>
            <Table
              loading={this.state.isLoading}
              scroll={{ x: 500 }}
              columns={ColumnsCreatorHOC()}
              dataSource={resultsWithKeys}
              pagination={{
                current: page,
                pageSize: this.state.pageSize,
                total: opportuntiesCount,
                onChange: this.nextPage,
                showSizeChanger: true,
                showTotal: showTotalPagination,
              }}
            />
          </Fragment>
        </Card>
        <AdvancedSearchModal
          model="opportunity"
          showModal={this.state.showAdvancedSearchModal}
          toggleModal={() => this.setState({ showAdvancedSearchModal: false })}
          onData={this.handleAdvancedSearch}
          onError={this.handleOnAdvancedSearchError}
          activeFields={selectedFieldsToDisplay}
        />
      </>
    );
  }
}
